import React, { memo, useState, useMemo, useEffect, useRef } from 'react';
import cn from 'classnames';

import { IconButton } from 'components';
import { ButtonV2, Input } from 'components/form';
import TickCheckbox from 'components/form/TickCheckbox';
import { useResponsive } from 'hooks/useResponsive';
import { Option } from 'lib/models/option';
import SubmitButton from '../SubmitButton';

import styles from '../Onboarding.module.scss';

interface Inputs {
  interestable_id: string;
}

export interface InterestsProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  topics: Array<Option>;
  candidateInterests: Array<Inputs>;
  onNextClick: (selectedInterests: Array<Inputs>) => void;
  onLeftClick: () => void;
  step: number;
  totalStep: number;
  isLoading: boolean;
  openOnModal?: boolean;
}

export const Interests: React.FC<InterestsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    topics,
    candidateInterests,
    onNextClick,
    onLeftClick,
    step,
    totalStep,
    isLoading,
    openOnModal = false,
  }: InterestsProps) => {
    const contentClassNames = cn(styles.container, className);
    const [scrolled, setScrolled] = useState(false);
    const [searchScrolled, setSearchScrolled] = useState(false);
    const [isStickySearch, setStickySearch] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const screens = useResponsive();

    const containerElement = containerRef.current;
    const [search, setSearch] = useState('');
    const [selectedInterests, setSelectedInterests] = useState<Array<Inputs>>(
      []
    );

    useEffect(() => {
      if (candidateInterests) setSelectedInterests(candidateInterests);
    }, [candidateInterests]);

    const trimmedSearch = search.trim();
    const filteredOptions = useMemo(() => {
      return search
        ? topics.filter(
            (topic) =>
              topic.label.toLowerCase().indexOf(trimmedSearch.toLowerCase()) >
              -1
          )
        : topics;
    }, [trimmedSearch, topics, search]);

    const onSubmit = () => {
      onNextClick(selectedInterests);
    };

    const onInterestChange = (id: string) => {
      const index = selectedInterests?.findIndex(
        (interest) => interest.interestable_id === id
      );
      if (index === -1) {
        setSelectedInterests([
          ...selectedInterests,
          {
            interestable_id: id,
          },
        ]);
      } else {
        const modifiedInterests = selectedInterests.filter(
          (interest) => interest.interestable_id !== id
        );
        setSelectedInterests(modifiedInterests);
      }
    };

    const isReady = selectedInterests.length > 0;

    useEffect(() => {
      if (!containerElement) return;

      function handler() {
        if (!containerElement) return;
        setScrolled(containerElement.scrollTop > 50);
        setSearchScrolled(containerElement.scrollTop > 150);
      }

      containerElement.addEventListener('scroll', handler);

      return () => {
        containerElement.removeEventListener('scroll', handler);
      };
    }, [containerElement]);

    return (
      <>
        <div
          className={contentClassNames}
          style={style}
          data-testid="Interests"
          ref={containerRef}
        >
          <div
            className={cn(
              { [styles.content]: !openOnModal },
              { [styles.modalContent]: openOnModal }
            )}
          >
            {openOnModal ? null : (
              <div className={styles.header}>
                <div className={styles.navigation}>
                  <IconButton
                    iconName="chevron-left"
                    size="xxlarge"
                    onClick={onLeftClick}
                    className={styles.chevron}
                  />
                  {!scrolled && (
                    <div className={styles.step}>
                      {step} / {totalStep}
                    </div>
                  )}
                  <IconButton
                    iconName="chevron-right"
                    size="xxlarge"
                    className={cn(styles.chevron, {
                      [styles.chevronDisabled]: !isReady,
                    })}
                    onClick={onSubmit}
                  />
                </div>
                <p className={cn(styles.title, { [styles.hide]: !scrolled })}>
                  Add your topic
                </p>
                <IconButton
                  iconName="search"
                  size="large"
                  onClick={() => setStickySearch((search) => !search)}
                  className={cn(styles.minisearch, {
                    [styles.hide]: !searchScrolled,
                    [styles.open]: isStickySearch,
                  })}
                />
              </div>
            )}
            {openOnModal ? (
              <p className={styles.modalTitle}>Topics</p>
            ) : (
              <p className={styles.setup}>🍿 Add your Topics</p>
            )}
            <Input
              className={cn(
                styles.search,
                {
                  [styles.sticky]:
                    searchScrolled && isStickySearch && !openOnModal,
                },
                { [styles.modalSearch]: openOnModal }
              )}
              placeholder="Search"
              startIcon="search"
              startIconSize="small"
              endIconSize="xsmall"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endIcon={search ? 'cancel' : undefined}
              onEndIconClick={() => setSearch('')}
            />
            <div
              className={cn(styles.checkboxes, {
                [styles.modalCheckboxes]: openOnModal,
              })}
            >
              {filteredOptions.map((topic) => {
                return (
                  <TickCheckbox
                    key={topic.id}
                    checked={selectedInterests.some(
                      (interest) => interest.interestable_id === topic.id
                    )}
                    onChange={() => onInterestChange(topic.id)}
                  >
                    {topic.label}
                  </TickCheckbox>
                );
              })}
            </div>
            {openOnModal ? null : (
              <SubmitButton
                onClick={onSubmit}
                className={styles.button}
                isReady={isReady}
                isDisabled={!isReady}
                isLoading={isLoading}
              >
                Next
              </SubmitButton>
            )}
          </div>
        </div>
        {!openOnModal ? null : screens.sm ? (
          <ButtonV2
            isFullWidth={true}
            onClick={onSubmit}
            className={styles.doneMobile}
            isLoading={isLoading}
          >
            Done
          </ButtonV2>
        ) : (
          <section className={styles.done}>
            <div className={styles.separator}></div>
            <div className={styles.button}>
              <ButtonV2 width={128} onClick={onSubmit} isLoading={isLoading}>
                Done
              </ButtonV2>
            </div>
          </section>
        )}
      </>
    );
  }
);

Interests.displayName = 'Interests';
